import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { crmDataService } from "./utils/crmDataService";
import { CRM_CONSTANTS } from "./utils/crmConstants";
import TicketSidebar from "./components/todotickets/TicketSidebar";
import TicketDetailsPanel from "./components/todotickets/TicketDetailsPanel";
import TicketControlsPanel from "./components/todotickets/TicketControlsPanel";
import TicketReplyPanel from "./components/todotickets/TicketReplyPanel";
import InternalNotesPanel from "./components/todotickets/InternalNotesPanel";
import "./styles/toDoTickets.css";

function ToDoTickets({ teamFilter = null, teamCode = null }) {
  const { ticketNumber } = useParams();

  // State Management
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketFieldValues, setTicketFieldValues] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [comments, setComments] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [history, setHistory] = useState([]);
  const [teams, setTeams] = useState([]);
  const [stores, setStores] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [notes, setNotes] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const loadTicketFieldValues = async (ticketId) => {
    try {
      const data = await crmDataService.getTicketFieldValues(ticketId);

      // Transform nested to flat (matching TicketForm.js pattern)
      if (data) {
        const flatValues = {};
        Object.entries(data).forEach(([key, val]) => {
          flatValues[key] = val?.value ?? val;
        });
        setTicketFieldValues(flatValues);
      } else {
        setTicketFieldValues({});
      }
    } catch (err) {
      console.warn("No conditional field values found:", err);
      setTicketFieldValues({});
    }
  };

  const loadComments = async (ticketNumber) => {
    try {
      const data = await crmDataService.getTicketComments(ticketNumber);
      setComments(data || []);
    } catch (err) {
      console.warn("Failed to load comments:", err);
      setComments([]);
    }
  };

  const loadHistory = async (ticketNumber) => {
    try {
      const data = await crmDataService.getTicketHistory(ticketNumber);
      setHistory(data || []);
    } catch (err) {
      console.warn("Failed to load history:", err);
      setHistory([]);
    }
  };

  const loadCommunications = async (ticketNumber) => {
    try {
      // Using getTicketHistory as communications endpoint (they're the same on backend)
      const data = await crmDataService.getTicketHistory(ticketNumber);
      setCommunications(data || []);
    } catch (err) {
      console.warn("Failed to load communications:", err);
      setCommunications([]);
    }
  };

  const loadNotes = async (ticketNumber) => {
    try {
      const data = await crmDataService.getInternalNotes(ticketNumber);
      setNotes(data || []);
    } catch (err) {
      console.warn("Failed to load notes:", err);
      setNotes([]);
    }
  };

  const loadTicketByNumber = useCallback(async (ticketNum) => {
    try {
      setLoading(true);
      const ticketData = await crmDataService.getTicketByNumber(ticketNum);

      setSelectedTicket(ticketData);

      // Load additional ticket data in parallel
      await Promise.all([
        loadTicketFieldValues(ticketData.id),
        loadComments(ticketData.ticket_number),
        loadHistory(ticketData.ticket_number),
        loadCommunications(ticketData.ticket_number),
        loadNotes(ticketData.ticket_number),
      ]);

      setError(null);
    } catch (err) {
      console.error("Failed to load ticket:", err);
      setError(
        `${CRM_CONSTANTS.ERROR_MESSAGES.TICKET_NOT_FOUND}: ${err.message}`
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load initial data
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const searchCriteria = {
        limit: CRM_CONSTANTS.UI.TICKET_LOAD_LIMIT,
      };

      // Add team filter if provided, otherwise exclude TA tickets
      if (teamFilter) {
        searchCriteria.teamCode = teamFilter;
      } else {
        // By default, exclude Talent Acquisition tickets (they have their own view)
        searchCriteria.excludeTeamCode = "TA";
      }

      const [ticketsData, teamsData, storesData] = await Promise.all([
        crmDataService.searchTickets(searchCriteria),
        crmDataService.getTeams(),
        crmDataService.getStores(),
      ]);

      // Filter out TA tickets on frontend if no team filter specified
      // (backend excludeTeamCode may not be supported, so filter here as well)
      const filteredTickets = teamFilter
        ? ticketsData
        : ticketsData.filter(ticket =>
            ticket.team_code !== 'TA' && !ticket.ticket_number?.startsWith('TA-')
          );

      setTickets(filteredTickets);
      setTeams(teamsData);
      setStores(storesData);
      setError(null);
    } catch (err) {
      console.error("Failed to load initial data:", err);
      setError(
        `${CRM_CONSTANTS.ERROR_MESSAGES.DATABASE_ERROR}: ${err.message}`
      );
    } finally {
      setLoading(false);
    }
  }, [teamFilter, teamCode]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Check for ticket query parameter (from email links)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ticketParam = params.get('ticket');

    if (ticketParam && teams.length > 0) {
      loadTicketByNumber(ticketParam);
    }
  }, [teams, loadTicketByNumber]);

  // Load specific ticket if ticketNumber in URL
  useEffect(() => {
    if (ticketNumber && teams.length > 0) {
      loadTicketByNumber(ticketNumber);
    }
  }, [ticketNumber, teams, loadTicketByNumber]);

  const handleTicketSelect = async (ticket) => {
    await loadTicketByNumber(ticket.ticket_number);
  };

  const handleRefreshTickets = async () => {
    try {
      const searchCriteria = {
        limit: CRM_CONSTANTS.UI.TICKET_LOAD_LIMIT,
      };

      // Add team filter if provided, otherwise exclude TA tickets
      if (teamFilter) {
        searchCriteria.teamCode = teamFilter;
      } else {
        // By default, exclude Talent Acquisition tickets (they have their own view)
        searchCriteria.excludeTeamCode = "TA";
      }

      const ticketsData = await crmDataService.searchTickets(searchCriteria);

      // Filter out TA tickets on frontend if no team filter specified
      const filteredTickets = teamFilter
        ? ticketsData
        : ticketsData.filter(ticket =>
            ticket.team_code !== 'TA' && !ticket.ticket_number?.startsWith('TA-')
          );

      setTickets(filteredTickets);
      setSuccessMessage("Tickets refreshed");
      setTimeout(
        () => setSuccessMessage(""),
        CRM_CONSTANTS.UI.SUCCESS_MESSAGE_DURATION
      );
    } catch (err) {
      setError(
        `${CRM_CONSTANTS.ERROR_MESSAGES.DATABASE_ERROR}: ${err.message}`
      );
    }
  };

  const handleTicketUpdate = async (ticketNumber, updateData) => {
    try {
      await crmDataService.updateTicket(ticketNumber, updateData);
      setSuccessMessage(CRM_CONSTANTS.SUCCESS_MESSAGES.TICKET_UPDATED);
      setTimeout(
        () => setSuccessMessage(""),
        CRM_CONSTANTS.UI.SUCCESS_MESSAGE_DURATION
      );

      // Reload ticket data
      if (selectedTicket) {
        await loadTicketByNumber(selectedTicket.ticket_number);
      }
      // Refresh ticket list
      await handleRefreshTickets();
    } catch (err) {
      setError(
        `${CRM_CONSTANTS.ERROR_MESSAGES.DATABASE_ERROR}: ${err.message}`
      );
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleAddComment = async (ticketNumber, commentData) => {
    try {
      await crmDataService.addTicketComment(ticketNumber, commentData);
      setSuccessMessage(CRM_CONSTANTS.SUCCESS_MESSAGES.COMMENT_ADDED);
      setTimeout(
        () => setSuccessMessage(""),
        CRM_CONSTANTS.UI.SUCCESS_MESSAGE_DURATION
      );
      // Reload comments using ticket number
      if (selectedTicket) {
        await loadComments(selectedTicket.ticket_number);
      }
    } catch (err) {
      setError(
        `${CRM_CONSTANTS.ERROR_MESSAGES.DATABASE_ERROR}: ${err.message}`
      );
    }
  };

  const handleSendReply = async (ticketNumber, emailData) => {
    try {
      // Send ticket reply email - this will send email AND log the conversation
      await crmDataService.sendTicketReply(ticketNumber, {
        subject: emailData.subject,
        message: emailData.body,
        ccEmails: emailData.cc,
        senderEmail: localStorage.getItem('userEmail') || 'no-reply@cafuamanagement.com',
        senderName: localStorage.getItem('userName') || 'HR Team'
      });
      setSuccessMessage(CRM_CONSTANTS.SUCCESS_MESSAGES.REPLY_SENT);
      setTimeout(
        () => setSuccessMessage(""),
        CRM_CONSTANTS.UI.SUCCESS_MESSAGE_DURATION
      );
      // Reload communications using ticket number
      if (selectedTicket) {
        await loadCommunications(selectedTicket.ticket_number);
      }
    } catch (err) {
      setError(
        `${CRM_CONSTANTS.ERROR_MESSAGES.DATABASE_ERROR}: ${err.message}`
      );
    }
  };

  const handleAddNote = async (ticketNumber, noteData) => {
    try {
      await crmDataService.addInternalNote(ticketNumber, noteData);
      setSuccessMessage(CRM_CONSTANTS.SUCCESS_MESSAGES.NOTE_SAVED);
      setTimeout(
        () => setSuccessMessage(""),
        CRM_CONSTANTS.UI.SUCCESS_MESSAGE_DURATION
      );
      // Reload notes using ticket number
      if (selectedTicket) {
        await loadNotes(selectedTicket.ticket_number);
      }
    } catch (err) {
      setError(
        `${CRM_CONSTANTS.ERROR_MESSAGES.DATABASE_ERROR}: ${err.message}`
      );
    }
  };

  // Filter tickets by team code if provided
  const getFilteredTickets = () => {
    if (!teamCode) return tickets;
    return tickets.filter(ticket => ticket.team_code === teamCode);
  };

  const filteredTickets = getFilteredTickets();

  if (loading && !selectedTicket) {
    return (
      <div className="todo-tickets-loading">
        <div className="loading-spinner"></div>
        <p>Loading To Do Tickets...</p>
      </div>
    );
  }

  return (
    <div className="todo-tickets-container">
      {/* Alert Messages */}
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span>{successMessage}</span>
        </div>
      )}

      <div className="todo-tickets-layout">
        {/* Left Sidebar */}
        <TicketSidebar
          tickets={filteredTickets}
          selectedTicket={selectedTicket}
          onTicketSelect={handleTicketSelect}
          onRefresh={handleRefreshTickets}
          stores={stores}
        />

        {/* Main Content Area */}
        <div className="todo-tickets-main">
          {selectedTicket ? (
            <>
              {/* Top Row: Details (left) + Controls (right) */}
              <div className="todo-tickets-top-row">
                <TicketDetailsPanel
                  ticket={selectedTicket}
                  fieldValues={ticketFieldValues}
                />

                <TicketControlsPanel
                  ticket={selectedTicket}
                  teams={teams}
                  stores={stores}
                  onUpdate={(updateData) =>
                    handleTicketUpdate(selectedTicket.ticket_number, updateData)
                  }
                />
              </div>

              {/* Bottom Row: Reply (left) + Notes (right) */}
              <div className="todo-tickets-bottom-row">
                <TicketReplyPanel
                  ticket={selectedTicket}
                  communications={communications}
                  onSendReply={(emailData) =>
                    handleSendReply(selectedTicket.ticket_number, emailData)
                  }
                />

                <InternalNotesPanel
                  ticket={selectedTicket}
                  notes={notes}
                  onAddNote={(noteData) =>
                    handleAddNote(selectedTicket.ticket_number, noteData)
                  }
                />
              </div>
            </>
          ) : (
            <div className="todo-tickets-empty">
              <div className="empty-icon">📋</div>
              <h2>Select a Ticket</h2>
              <p>
                Choose a ticket from the sidebar to view details and manage it
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ToDoTickets;